import React from "react";
import { Link } from "react-router";

import { ColumnDef } from "@tanstack/react-table";

import { enrichmentStatusColumn } from "@/components/common/status-columns";
import { UserAvatar } from "@/components/common/user-avatar";
import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { IMovieData } from "@/types/movie";

function releaseYear(raw: string | number | undefined): string {
  if (typeof raw === "string" && raw.length >= 4) return raw.slice(0, 4);
  if (typeof raw === "number")
    return String(new Date(raw * 1000).getUTCFullYear());
  return "—";
}

export const useMovieColumns = () => {
  const { members } = useMembers();
  return React.useMemo<ColumnDef<IMovieData>[]>(
    () => [
      {
        id: "id",
        accessorKey: "document.id",
        header: "ID",
      },
      {
        id: "poster",
        header: "",
        accessorFn: (row) => row.document.media?.photos?.[0],
        cell: function render({ getValue }: any) {
          const photo = getValue();
          return photo?.url ? (
            <PhotoWithBlurhash
              className="h-12 w-8 rounded object-cover"
              url={photo.url}
              blurhash={photo.blurhash ?? ""}
            />
          ) : (
            <div className="h-12 w-8 rounded bg-muted" />
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        size: 56,
      },
      {
        id: "title",
        accessorFn: (row) =>
          row.highlights?.filter((h: any) => h.field === "title")[0]?.snippet ??
          row.document.title,
        header: "Title",
        minSize: 200,
        cell: function render({ row, getValue }: any) {
          return makeTooltip(
            <Link
              to={`/movies/show/${row.original.document.id}`}
              className="block truncate font-medium hover:underline"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            />,
          );
        },
        enableSorting: true,
        size: 280,
      },
      {
        id: "releaseDate",
        header: "Year",
        accessorFn: (row) => releaseYear(row.document.releaseDate),
        enableColumnFilter: false,
        size: 80,
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "document.status",
        cell: function render({ getValue }: any) {
          const status = getValue();
          return status ? <Badge variant="default">{status}</Badge> : null;
        },
        enableSorting: false,
        size: 110,
        meta: {
          filterType: "select",
          options: [
            { label: "Draft", value: "Draft" },
            { label: "Complete", value: "Complete" },
          ],
        },
      },
      // Immediately after Status — the shared definition. A movie is NOT
      // embeddable, so there is no embedding column.
      enrichmentStatusColumn<IMovieData>(),
      {
        id: "genres",
        header: "Genres",
        accessorFn: (row) =>
          (row.document.genres ?? []).map((g) => g.name).join(", "),
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "createdBy",
        header: "Created By",
        minSize: 170,
        accessorKey: "document.createdBy",
        cell: function render({ getValue }: any) {
          const value = getValue();
          const member = members.find(
            (x) =>
              x.id === value || x.adminMetadata?.relatedIds?.includes(value),
          );
          return member ? (
            <div className="p-1">
              <UserAvatar {...member} half />
            </div>
          ) : (
            value
          );
        },
        enableSorting: false,
      },
      {
        id: "createdAt",
        header: "Created At",
        // `createdAt` is optional on IMovie, and `parseUnixTimestamp(undefined)`
        // is both a type error and an "Invalid Date" cell. Read it as null and
        // render an em-dash instead.
        accessorFn: (row) =>
          row.document.createdAt == null
            ? null
            : parseUnixTimestamp(row.document.createdAt),
        cell: function render({ getValue }: any) {
          const value = getValue();
          if (!value)
            return <span className="text-muted-foreground/60">—</span>;
          return makeTooltip(
            value.format("DD/MM/YYYY HH:mm"),
            value.format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        enableColumnFilter: true,
        meta: { filterType: "date" },
      },
      {
        id: "actions",
        header: "Actions",
        cell: function render({ row }) {
          return (
            <div className="flex items-center gap-1">
              <ShowButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
              <EditButton
                size="sm"
                hideText={true}
                recordItemId={row.original.document.id}
              />
            </div>
          );
        },
        enableSorting: false,
        enablePinning: true,
        size: 100,
      },
    ],
    [members],
  );
};
